/**
 * ConsentGate — blocks app access until user accepts Terms + Privacy.
 * Used for existing users who were created before the consent system.
 * New users go through onboarding flow which collects consent inline.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Shield } from 'lucide-react';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { AppLayoutSkeleton } from '@/components/ui/skeletons';
import { logError } from '@/lib/logger';
import { useConsentStatus, consentStatusQueryKey } from './useConsentStatus';
import { useRole } from '@/contexts/RoleContext';

interface ConsentGateProps {
  children: React.ReactNode;
}

export function ConsentGate({ children }: ConsentGateProps) {
  const { t } = useTranslation(['onboarding']);
  const { authenticatedFetch } = useAuthFetch();
  const { selectedOrgId } = useRole();
  const queryClient = useQueryClient();
  const { data: status, isLoading } = useConsentStatus();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGrant = async () => {
    setIsSubmitting(true);
    setError(null);

    // Retry only on TypeError ("Failed to fetch") — the request never reached
    // the server (or response was lost). authenticatedFetch's built-in retry
    // skips POSTs because /consent/grant is event-log insert, not idempotent,
    // so we retry locally and only on this safe class of errors.
    const grant = (consentType: 'terms_of_service' | 'privacy_policy') =>
      authenticatedFetch('/api/v1/consent/grant', {
        method: 'POST',
        body: JSON.stringify({
          consentType,
          metadata: { version: '2026-02-21', source: 'consent_gate' },
        }),
      });

    const grantWithRetry = async (consentType: 'terms_of_service' | 'privacy_policy') => {
      const delaysMs = [0, 300, 900];
      let lastError: unknown;
      for (const delay of delaysMs) {
        if (delay > 0) await new Promise((r) => setTimeout(r, delay));
        try {
          return await grant(consentType);
        } catch (err) {
          lastError = err;
          const isNetworkError = err instanceof TypeError && /fetch/i.test(err.message);
          if (!isNetworkError) throw err;
        }
      }
      throw lastError;
    };

    try {
      const grants: Promise<unknown>[] = [];

      if (!status?.terms_of_service) {
        grants.push(grantWithRetry('terms_of_service'));
      }

      if (!status?.privacy_policy) {
        grants.push(grantWithRetry('privacy_policy'));
      }

      await Promise.all(grants);
      queryClient.setQueryData(consentStatusQueryKey(selectedOrgId), {
        ...(status ?? { complianceConfirmed: false }),
        terms_of_service: true,
        privacy_policy: true,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('consentGate.failedToSave');
      setError(message);
      logError('consent.grant_failed', {
        component: 'ConsentGate',
        error: message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Defensive skeleton: MainAppLayout normally keeps the splash visible until
  // this query resolves, but on stale-cache refetch or org switch we still
  // prefer a skeleton over a blank frame.
  if (isLoading || !status) {
    return <AppLayoutSkeleton />;
  }

  // Consent already granted — render app
  if (status.terms_of_service && status.privacy_policy) {
    return <>{children}</>;
  }

  // Show consent screen
  const canContinue = acceptedTerms && acceptedPrivacy;

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-void p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary-dim mb-4">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold text-fg mb-2">
            {t('consentGate.title', 'Updated Terms & Privacy')}
          </h2>
          <p className="text-fg-muted">
            {t(
              'consentGate.description',
              'Please review and accept our updated agreements to continue.'
            )}
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <label className="flex items-start gap-3 min-h-11 cursor-pointer">
            <Checkbox
              checked={acceptedTerms}
              onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
              className="mt-0.5"
            />
            <span className="text-sm text-fg-muted">
              {t('consent.termsLabel', 'I agree to the')}{' '}
              <a
                href="/terms"
                target="_blank"
                rel={['noopener', 'noreferrer'].join(' ')}
                className="text-primary hover:underline"
              >
                {t('consent.termsLink', 'Terms of Service')}
              </a>
            </span>
          </label>

          <label className="flex items-start gap-3 min-h-11 cursor-pointer">
            <Checkbox
              checked={acceptedPrivacy}
              onCheckedChange={(checked) => setAcceptedPrivacy(checked === true)}
              className="mt-0.5"
            />
            <span className="text-sm text-fg-muted">
              {t('consent.privacyLabel', 'I agree to the')}{' '}
              <a
                href="/privacy"
                target="_blank"
                rel={['noopener', 'noreferrer'].join(' ')}
                className="text-primary hover:underline"
              >
                {t('consent.privacyLink', 'Privacy Policy')}
              </a>
            </span>
          </label>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-danger-dim border border-danger mb-4">
            <p className="text-sm text-danger-fg">{error}</p>
          </div>
        )}

        <Button
          onClick={handleGrant}
          disabled={!canContinue || isSubmitting}
          size="lg"
          // Hero call-to-action: 48px, taller than the lg step (40px) — pre-existing, no variant for it yet.
          className="w-full min-h-12 gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('consentGate.saving', 'Saving...')}
            </>
          ) : (
            t('consentGate.continue', 'Accept & Continue')
          )}
        </Button>
      </div>
    </div>
  );
}
